<script>
  import { onMount } from "svelte";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import { onSubmitUnsubscribe } from "lib/modules/Contact.svelte";

  const params = new URLSearchParams(window.location.search);
  const email = params.get("email");

  let state = email ? "loading" : "invalid";

  async function handleUnsubscribe(token) {
    try {
      await onSubmitUnsubscribe({ email, token });
      state = "success";
    } catch {
      state = "error";
    }
  }

  onMount(() => {
    console.log("[Unsubscribe] onMount fired, state:", state);
    if (state !== "loading") {
      return;
    }
    const execute = () => {
      console.log(
        "[Unsubscribe] execute() called, grecaptcha:",
        typeof grecaptcha,
      );
      grecaptcha.ready(async () => {
        console.log("[Unsubscribe] grecaptcha.ready() fired");
        try {
          const token = await grecaptcha.execute("RECAPTCHA_SITE_KEY", {
            action: "unsubscribe",
          });
          console.log(
            "[Unsubscribe] token received:",
            token ? token.substring(0, 20) + "..." : token,
          );
          await handleUnsubscribe(token);
        } catch (err) {
          console.error("[Unsubscribe] grecaptcha.execute() failed:", err);
          state = "error";
        }
      });
    };
    console.log("[Unsubscribe] typeof grecaptcha:", typeof grecaptcha);
    if (typeof grecaptcha !== "undefined") {
      execute();
    } else {
      console.log(
        "[Unsubscribe] grecaptcha not ready, setting window.onRecaptchaLoad",
      );
      window.onRecaptchaLoad = execute;
    }
  });
</script>

<svelte:head>
  <title>Unsubscribe - Liquid Noble</title>
  <meta name="robots" content="noindex" />
</svelte:head>

<Header />

<Container variant="dark" size="large" class="unsubscribe-container">
  <div class="unsubscribe-card">
    {#if state === "invalid"}
      <div class="status-icon error-icon">✕</div>
      <h1>Invalid link</h1>
      <p>
        This unsubscribe link appears to be incomplete. Please use the link from
        your email, or contact us at <a href="mailto:hello@liquidnoble.com"
          >hello@liquidnoble.com</a
        > and we'll take care of it.
      </p>
    {:else if state === "loading"}
      <div class="spinner" aria-label="Processing"></div>
      <p class="loading-text">Just a moment&hellip;</p>
    {:else if state === "success"}
      <div class="status-icon success-icon">✓</div>
      <h1>You've been unsubscribed</h1>
      <p>
        We've removed <strong>{email}</strong> from our marketing emails. You'll
        still receive essential emails related to your account and orders.
      </p>
    {:else if state === "error"}
      <div class="status-icon error-icon">✕</div>
      <h1>Something went wrong</h1>
      <p>
        We weren't able to process your unsubscribe request. Please try again
        later, or contact us at <a href="mailto:hello@liquidnoble.com"
          >hello@liquidnoble.com</a
        > if the problem persists.
      </p>
    {/if}
  </div>
</Container>

<Footer />

<style lang="scss">
  .unsubscribe-container {
    min-height: 60vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .unsubscribe-card {
    max-width: 520px;
    margin: 0 auto;
    padding: 4rem 2rem;
    text-align: center;
  }

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-color1);
    margin: 0 0 1.25rem;
    line-height: 1.2;
  }

  p {
    font-size: 1.05rem;
    color: var(--text-color2);
    line-height: 1.7;
    margin: 0 0 1rem;

    strong {
      color: var(--text-color1);
      font-weight: 600;
    }

    a {
      color: var(--text-color1);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .subdued {
    font-size: 0.9rem;
    opacity: 0.6;
    margin-top: 1.5rem;
  }

  .status-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: bold;
    margin: 0 auto 2rem;
  }

  .success-icon {
    background: var(--text-color1);
    color: var(--background-color, #1e0f30);
  }

  .error-icon {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #ef4444;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: var(--text-color1);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 1.5rem;
  }

  .loading-text {
    opacity: 0.6;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 480px) {
    .unsubscribe-card {
      padding: 3rem 1rem;
    }

    h1 {
      font-size: 1.6rem;
    }

    p {
      font-size: 1rem;
    }
  }
</style>
